import React, { useState } from 'react';
import Button from '../../ui/Button';
import { sendInvitationEmail } from '../../../services/emailService';
import { EnvelopeIcon, CheckCircleIcon, ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { formatPhone, isValidEmail } from '../../../utils/formatUtils';

const ProfessionalsStep = ({ onNext, onBack, initialData = {}, categories = [], isLoading = false }) => {
  const [professionals, setProfessionals] = useState(initialData.professionals || []);
  const [showForm, setShowForm] = useState(false);
  const [currentProfessional, setCurrentProfessional] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState({});
  const [importError, setImportError] = useState(null);
  const [successToast, setSuccessToast] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [emailList, setEmailList] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  const [editingEmailIndex, setEditingEmailIndex] = useState(null);
  const [bulkInviteStatus, setBulkInviteStatus] = useState({ loading: false, results: [] });
  
  const [professionalForm, setProfessionalForm] = useState({
    name: '',
    email: '',
    phone: '',
    photo: null,
    photoURL: '',
    bio: '',
    serviceIds: [],
    inviteSent: false
  });
  
  // Lista de todos os serviços agrupados por categoria para seleção
  const allServices = categories.flatMap(category => 
    category.services.map(service => ({
      id: `${category.name}-${service.name}`, // Criar um ID único para o serviço
      name: service.name,
      categoryName: category.name,
      duration: service.duration,
      price: service.price
    }))
  );
  
  const handleAddProfessional = () => {
    setShowForm(true);
    setCurrentProfessional(null);
    setProfessionalForm({
      name: '',
      email: '',
      phone: '',
      photo: null,
      photoURL: '',
      bio: '',
      serviceIds: [],
      inviteSent: false
    });
  };
  
  const handleEditProfessional = (index) => {
    setShowForm(true);
    setCurrentProfessional(index);
    setProfessionalForm(professionals[index]);
  };
  
  const handleDeleteProfessional = (index) => {
    if (window.confirm('Tem certeza que deseja excluir este profissional?')) {
      console.log(`[handleDeleteProfessional] Excluindo profissional no índice ${index}:`, professionals[index]);
      const newProfessionals = [...professionals];
      newProfessionals.splice(index, 1);
      setProfessionals(newProfessionals);
      console.log(`[handleDeleteProfessional] Nova lista com ${newProfessionals.length} profissionais`);
    }
  };
  
  // Effect para logar o estado dos profissionais quando mudar
  React.useEffect(() => {
    console.log('[useEffect] Estado atual de profissionais:', professionals);
  }, [professionals]);

  // Efeito para rastrear atualizações de inviteStatus
  React.useEffect(() => {
    console.log('[useEffect] Estado atual de inviteStatus:', inviteStatus);
  }, [inviteStatus]);
  
  const validateForm = () => {
    const errors = {};
    
    // Validar email (apenas se foi fornecido)
    if (professionalForm.email && !isValidEmail(professionalForm.email)) {
      errors.email = 'O email não é válido';
    }
    
    // Validar nome
    if (!professionalForm.name.trim()) {
      errors.name = 'O nome é obrigatório';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Formatar telefone durante digitação
      setProfessionalForm({
        ...professionalForm,
        [name]: formatPhone(value)
      });
    } else if (name === 'email') {
      // Limpar erro de email ao digitar
      if (formErrors.email) {
        setFormErrors({
          ...formErrors,
          email: null
        });
      }
      setProfessionalForm({
        ...professionalForm,
        [name]: value
      });
    } else {
      // Para outros campos
      if (formErrors[name]) {
        setFormErrors({
          ...formErrors,
          [name]: null
        });
      }
      setProfessionalForm({
        ...professionalForm,
        [name]: value
      });
    }
  };
  
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Criar uma URL para pré-visualização da imagem
    const photoURL = URL.createObjectURL(file);
    
    setProfessionalForm({
      ...professionalForm,
      photo: file,
      photoURL
    });
  };
  
  const handleServiceToggle = (serviceId) => {
    setProfessionalForm(prev => {
      const isSelected = prev.serviceIds.includes(serviceId);
      let updatedServiceIds;
      
      if (isSelected) {
        updatedServiceIds = prev.serviceIds.filter(id => id !== serviceId);
      } else {
        updatedServiceIds = [...prev.serviceIds, serviceId];
      }
      
      return {
        ...prev,
        serviceIds: updatedServiceIds
      };
    });
  };
  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    // Validar formulário antes de salvar
    if (!validateForm()) {
      return;
    }
    
    if (currentProfessional !== null) {
      // Editando profissional existente
      const newProfessionals = [...professionals];
      newProfessionals[currentProfessional] = professionalForm;
      setProfessionals(newProfessionals);
    } else {
      // Adicionando novo profissional
      setProfessionals([...professionals, professionalForm]);
    }
    
    setShowForm(false);
  };
  
  const handleSendInvite = async (professional, index) => {
    try {
      console.log(`[handleSendInvite] Iniciando envio de convite para índice ${index}:`, professional);
      setSendingInvite(true);
      setInviteStatus(prev => ({
        ...prev,
        [index]: { loading: true }
      }));

      // Simular envio de convite durante o setup
      // No setup real, isso será substituído pelo método createUserInvitation
      const mockInvitation = {
        email: professional.email,
        role: 'professional',
        token: `mock-token-${Date.now()}`,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      };

      // Dados do salão que serão válidos após a conclusão do setup
      const mockSalonData = {
        name: initialData.businessName || 'Novo Salão',
        logoUrl: ''
      };

      // Enviar convite usando o emailService
      const response = await sendInvitationEmail(
        mockInvitation,
        mockSalonData,
        initialData.name || 'Proprietário'
      );
      console.log(`[handleSendInvite] Resposta do envio de email:`, response);

      // Usar o token do convite para garantir consistência
      const inviteToken = response.token || mockInvitation.token;

      // Atualizar status do convite
      setInviteStatus(prev => {
        console.log(`[setInviteStatus] Atualizando status para índice ${index}:`, {
          success: true, 
          message: 'Convite enviado com sucesso!',
          token: inviteToken,
          inviteUrl: response.inviteUrl
        });
        return {
          ...prev,
          [index]: { 
            success: true, 
            message: 'Convite enviado com sucesso!',
            token: inviteToken,
            inviteUrl: response.inviteUrl
          }
        };
      });

      // Atualizar status do profissional
      const updatedProfessionals = [...professionals];
      updatedProfessionals[index] = {
        ...professional,
        inviteSent: true,
        invitationToken: inviteToken
      };
      console.log(`[handleSendInvite] Atualizando profissional no índice ${index}:`, updatedProfessionals[index]);
      console.log(`[handleSendInvite] Lista completa após atualização:`, updatedProfessionals);

      setProfessionals(updatedProfessionals);
      
      // Mostrar toast de sucesso
      setSuccessToast({
        message: `Convite enviado para ${professional.name}!`,
        email: professional.email
      });
      
      // Esconder toast após 5 segundos
      setTimeout(() => {
        setSuccessToast(null);
      }, 5000);
      
      return response;
    } catch (error) {
      console.error(`[handleSendInvite] Erro ao enviar convite para ${professional.email}:`, error);
      setInviteStatus(prev => ({
        ...prev,
        [index]: { 
          error: true, 
          message: error.message || 'Erro ao enviar convite' 
        }
      }));
    } finally {
      setSendingInvite(false);
    }
  };
  
  // Função para copiar link de convite para área de transferência
  const copyInviteLink = (token, email, professional, index) => {
    try {
      // Se não temos um token de convite e precisamos gerar um temporário
      let inviteToken = token;
      if (!inviteToken) {
        inviteToken = `temp-token-${Date.now()}`;
        
        // Atualizar o status do profissional com o token temporário
        if (index !== undefined) {
          const updatedProfessionals = [...professionals];
          updatedProfessionals[index] = {
            ...professional,
            tempInvitationToken: inviteToken
          };
          setProfessionals(updatedProfessionals);
        }
      }
      
      const inviteLink = `${window.location.origin}/accept-invite/${inviteToken}?email=${encodeURIComponent(email)}`;
      navigator.clipboard.writeText(inviteLink);
      
      // Mostrar toast de sucesso por 3 segundos
      setSuccessToast({
        message: "Link de convite copiado!",
        email: email
      });
      
      setTimeout(() => {
        setSuccessToast(null);
      }, 3000);
    } catch (err) {
      console.error('Erro ao copiar link:', err);
      // Mostrar algum feedback de erro
      setSuccessToast({
        message: "Erro ao copiar link",
        email: email,
        isError: true
      });
      
      setTimeout(() => {
        setSuccessToast(null);
      }, 3000);
    }
  };
  
  const handleSendAllInvites = async () => {
    const professionals_with_email = professionals.filter(prof => prof.email && !prof.inviteSent);
    
    if (professionals_with_email.length === 0) {
      return;
    }
    
    // Confirmar antes de enviar múltiplos convites
    if (!window.confirm(`Enviar convites para ${professionals_with_email.length} profissionais?`)) {
      return;
    }
    
    setSendingInvite(true);
    
    for (let i = 0; i < professionals_with_email.length; i++) {
      const prof = professionals_with_email[i];
      const index = professionals.findIndex(p => p.email === prof.email);
      
      if (index !== -1) {
        try {
          await handleSendInvite(prof, index);
        } catch (error) {
          console.error(`Erro ao enviar convite para ${prof.email}:`, error);
        }
      }
    }
    
    setSendingInvite(false);
  };
  
  const handleFormCancel = () => {
    setShowForm(false);
  };
  
  const handleContinue = () => {
    console.log("[ProfessionalsStep] Enviando profissionais para o próximo passo:", {
      count: professionals.length,
      data: professionals
    });
    onNext({ professionals });
  };
  
  // Agrupar serviços por categoria para exibição
  const servicesByCategory = allServices.reduce((acc, service) => {
    if (!acc[service.categoryName]) {
      acc[service.categoryName] = [];
    }
    acc[service.categoryName].push(service);
    return acc;
  }, {});
  
  // Obter os serviços de um profissional
  const getProfessionalServices = (serviceIds) => {
    return allServices.filter(service => serviceIds.includes(service.id));
  };
  
  // Contar profissionais com email que ainda não receberam convite
  const pendingInvitesCount = professionals.filter(prof => prof.email && !prof.inviteSent).length;

  // Processar o email atual e adicionar à lista
  const processCurrentEmail = () => {
    const email = currentEmail.trim();
    
    if (!email) return;
    
    // Verifica se é um email válido
    const isValid = isValidEmail(email);
    
    if (isValid) {
      // Adiciona à lista de emails se não existir ainda
      if (!emailList.some(item => item.email === email)) {
        setEmailList(prev => [...prev, { email, isValid }]);
      }
      setCurrentEmail('');
    } else {
      // Adiciona como inválido se o usuário forçar (com espaço/vírgula)
      setEmailList(prev => [...prev, { email, isValid }]);
      setCurrentEmail('');
    }
  };
  
  // Lidar com input de email
  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    
    // Verificar se o último caractere é espaço ou vírgula
    if (value.endsWith(' ') || value.endsWith(',')) {
      // Remover o espaço ou vírgula e processar o email
      setCurrentEmail(value.slice(0, -1));
      processCurrentEmail();
    } else {
      setCurrentEmail(value);
    }
  };
  
  // Lidar com o keydown para processar quando pressionar Enter
  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processCurrentEmail();
    } else if (e.key === 'Backspace' && currentEmail === '' && emailList.length > 0) {
      // Remover o último email da lista se backspace for pressionado em um campo vazio
      setEditingEmailIndex(emailList.length - 1);
    }
  };
  
  // Remover um email da lista
  const removeEmail = (index) => {
    setEmailList(prev => prev.filter((_, i) => i !== index));
  };
  
  // Editar um email da lista
  const editEmail = (index) => {
    setCurrentEmail(emailList[index].email);
    setEditingEmailIndex(index);
  };
  
  // Salvar email em edição
  const saveEditingEmail = () => {
    if (editingEmailIndex !== null) {
      const emailToUpdate = currentEmail.trim();
      
      if (emailToUpdate) {
        const isValid = isValidEmail(emailToUpdate);
        
        setEmailList(prev => {
          const newList = [...prev];
          newList.splice(editingEmailIndex, 1);
          
          // Verificar se já existe este email na lista
          if (!newList.some(item => item.email === emailToUpdate)) {
            newList.splice(editingEmailIndex, 0, { email: emailToUpdate, isValid });
          }
          
          return newList;
        });
      } else {
        // Se o campo estiver vazio, remover o email
        removeEmail(editingEmailIndex);
      }
      
      setCurrentEmail('');
      setEditingEmailIndex(null);
    }
  };
  
  // Focus no input quando abrir o modal
  const emailInputRef = React.useRef(null);
  
  React.useEffect(() => {
    if (showBulkInviteModal && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [showBulkInviteModal]);

  // Processa lista de emails em massa
  const processBulkEmails = () => {
    // Processar o email atual antes de continuar
    if (currentEmail.trim()) {
      processCurrentEmail();
    }
    
    // Extrair apenas os emails válidos
    return emailList
      .filter(item => item.isValid)
      .map(item => item.email);
  };
  
  // Enviar convites em massa para os emails
  const handleBulkInvite = async () => {
    const emails = processBulkEmails();
    console.log("[handleBulkInvite] Iniciando convite em massa para emails:", emails);
    
    if (emails.length === 0) {
      setBulkInviteStatus({
        ...bulkInviteStatus,
        error: 'Nenhum email válido encontrado'
      });
      return;
    }
    
    setBulkInviteStatus({ 
      loading: true, 
      results: [],
      totalCount: emails.length,
      processedCount: 0 
    });
    
    const results = [];
    // Criamos uma cópia local da lista atual de profissionais para manipulação
    let updatedProfessionals = [...professionals];
    
    for (let i = 0; i < emails.length; i++) {
      const email = emails[i];
      
      try {
        // Verificar se o email já está na lista de profissionais
        const existingProfIndex = updatedProfessionals.findIndex(p => p.email === email);
        let professional;
        let professionalIndex;
        
        if (existingProfIndex >= 0) {
          // Usar o profissional existente
          professional = updatedProfessionals[existingProfIndex];
          professionalIndex = existingProfIndex;
          console.log(`[handleBulkInvite] Usando profissional existente #${professionalIndex}:`, professional);
        } else {
          // Criar um novo profissional temporário
          professional = {
            name: email.split('@')[0], // Nome temporário baseado no email
            email: email,
            phone: '',
            serviceIds: [],
            inviteSent: false,
            isFromBulkInvite: true // Marcar como vindo de convite em massa
          };
          
          console.log(`[handleBulkInvite] Criando novo profissional:`, professional);
          
          // Adicionar à lista local de profissionais
          updatedProfessionals.push(professional);
          professionalIndex = updatedProfessionals.length - 1;
          console.log(`[handleBulkInvite] Adicionado na posição ${professionalIndex}, lista agora tem ${updatedProfessionals.length} profissionais`);
          
          // Atualizar o estado global para refletir mudanças imediatamente
          setProfessionals([...updatedProfessionals]);
        }
        
        // Enviar o convite
        console.log(`[handleBulkInvite] Enviando convite para índice ${professionalIndex}:`, professional.email);
        
        // Simular envio de convite durante o setup
        const mockInvitation = {
          email: professional.email,
          role: 'professional',
          token: `mock-token-${Date.now()}`,
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        };

        // Dados do salão que serão válidos após a conclusão do setup
        const mockSalonData = {
          name: initialData.businessName || 'Novo Salão',
          logoUrl: ''
        };

        // Enviar convite usando o emailService
        const response = await sendInvitationEmail(
          mockInvitation,
          mockSalonData,
          initialData.name || 'Proprietário'
        );
        console.log(`[handleBulkInvite] Resposta do envio de email para ${professional.email}:`, response);

        // Usar o token do convite para garantir consistência
        const inviteToken = response.token || mockInvitation.token;

        // Atualizar status do convite
        setInviteStatus(prev => {
          const newStatus = {
            ...prev,
            [professionalIndex]: { 
              success: true, 
              message: 'Convite enviado com sucesso!',
              token: inviteToken,
              inviteUrl: response.inviteUrl
            }
          };
          console.log(`[handleBulkInvite] Atualizando status para índice ${professionalIndex}:`, newStatus[professionalIndex]);
          return newStatus;
        });

        // Atualizar status do profissional na lista local
        updatedProfessionals[professionalIndex] = {
          ...updatedProfessionals[professionalIndex],
          inviteSent: true,
          invitationToken: inviteToken
        };
        
        console.log(`[handleBulkInvite] Atualizando profissional no índice ${professionalIndex}:`, updatedProfessionals[professionalIndex]);
        
        // Atualizar o estado global para refletir mudanças
        setProfessionals([...updatedProfessionals]);
        
        results.push({
          email,
          success: true,
          token: inviteToken
        });
      } catch (error) {
        console.error(`[handleBulkInvite] Erro ao convidar ${email}:`, error);
        results.push({
          email,
          success: false,
          error: error.message || 'Erro ao enviar convite'
        });
      }
      
      // Atualizar o contador de progresso
      setBulkInviteStatus(prev => ({
        ...prev,
        processedCount: i + 1,
        results: [...results]
      }));
    }
    
    console.log("[handleBulkInvite] Concluído com resultados:", results);
    console.log("[handleBulkInvite] Lista final de profissionais:", updatedProfessionals);
    
    setBulkInviteStatus({
      loading: false,
      results: results,
      totalCount: emails.length,
      processedCount: emails.length
    });
  };
  
  const handleCloseBulkInviteModal = () => {
    setBulkInviteStatus({ loading: false, results: [] });
    setBulkEmails('');
    setEmailList([]);
    setCurrentEmail('');
    setEditingEmailIndex(null);
    setShowBulkInviteModal(false);
  };

  return (
    <div className="animate-fadeIn">
      <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
        Adicione seus profissionais
      </h2>
      <p className="text-gray-600 text-center mb-8">
        Cadastre os profissionais que trabalham no seu negócio e envie convites para eles se juntarem à plataforma
      </p>

      {/* Toast de confirmação */}
      {successToast && (
        <div className={`fixed top-4 right-4 z-50 ${successToast.isError ? 'bg-red-100 border-red-500 text-red-700' : 'bg-green-100 border-green-500 text-green-700'} border-l-4 p-4 rounded shadow-lg animate-fadeIn flex items-start max-w-md`}>
          <div className="flex-shrink-0">
            <CheckCircleIcon className={`h-5 w-5 ${successToast.isError ? 'text-red-500' : 'text-green-500'}`} />
          </div>
          <div className="ml-3">
            <p className="font-medium">{successToast.message}</p>
            <p className="text-sm">{successToast.email}</p>
          </div>
          <button 
            onClick={() => setSuccessToast(null)}
            className={`ml-auto -mx-1.5 -my-1.5 ${successToast.isError ? 'bg-red-50 text-red-500 hover:bg-red-200' : 'bg-green-50 text-green-500 hover:bg-green-200'} rounded-lg p-1`}
          >
            <span className="sr-only">Fechar</span>
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {!showForm ? (
        <div className="mb-8">
          {professionals.length > 0 ? (
            <>
              {/* Opção para enviar todos os convites */}
              {pendingInvitesCount > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-blue-500 mr-3" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">
                        {pendingInvitesCount} {pendingInvitesCount === 1 ? 'profissional' : 'profissionais'} sem convite
                      </h4>
                      <p className="text-xs text-blue-600">
                        Você pode enviar convites individualmente ou todos de uma vez
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowBulkInviteModal(true)}
                      className="px-3 py-1.5 bg-white border border-blue-300 text-blue-600 text-sm font-medium rounded hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Convidar por email
                    </button>
                    <button
                      type="button"
                      onClick={handleSendAllInvites}
                      disabled={sendingInvite}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {sendingInvite ? 'Enviando...' : 'Enviar todos'}
                    </button>
                  </div>
                </div>
              )}
            
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {professionals.map((professional, index) => {
                  console.log(`[Renderização] Renderizando profissional #${index}:`, {
                    name: professional.name,
                    email: professional.email,
                    isFromBulkInvite: professional.isFromBulkInvite,
                    inviteSent: professional.inviteSent
                  });
                  return (
                    <div key={index} className={`bg-white rounded-lg border ${professional.isFromBulkInvite ? 'border-blue-200 bg-blue-50' : 'border-gray-200'} shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md`}>
                      <div className="flex items-center p-4">
                        <div className="flex-shrink-0">
                          {professional.photoURL ? (
                            <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100">
                              <img 
                                src={professional.photoURL} 
                                alt={professional.name} 
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : professional.isFromBulkInvite ? (
                            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                              <EnvelopeIcon className="h-8 w-8 text-blue-500" />
                            </div>
                          ) : (
                            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                              <svg className="h-8 w-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900">{professional.name}</h3>
                            {professional.isFromBulkInvite && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Convite
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{professional.email}</p>
                          {!professional.isFromBulkInvite && professional.phone && (
                            <p className="text-sm text-gray-500">{professional.phone}</p>
                          )}
                        </div>
                        <div className="flex flex-col space-y-2">
                          {!professional.isFromBulkInvite && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditProfessional(index)}
                                className="p-1 text-blue-600 hover:text-blue-800"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => handleDeleteProfessional(index)}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {!professional.isFromBulkInvite && professional.serviceIds && professional.serviceIds.length > 0 && (
                        <div className="px-4 pb-4">
                          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            Serviços
                          </h4>
                          <div className="flex flex-wrap gap-1">
                            {getProfessionalServices(professional.serviceIds).map((service, serviceIndex) => (
                              <span 
                                key={serviceIndex}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {service.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {professional.email && (
                        <div className={`px-4 pb-4 mt-2 border-t ${professional.isFromBulkInvite ? 'border-blue-100' : 'border-gray-100'} pt-3`}>
                          {professional.inviteSent ? (
                            <div className="flex items-center justify-between">
                              <div className={`flex items-center text-sm ${professional.isFromBulkInvite ? 'text-blue-600' : 'text-green-500'}`}>
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {professional.isFromBulkInvite ? 'Convite enviado por email' : 'Convite enviado'}
                              </div>
                              
                              {/* Botão para copiar link */}
                              {inviteStatus[index]?.token || professional.invitationToken ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); copyInviteLink(inviteStatus[index]?.token || professional.invitationToken, professional.email, professional, index); }}
                                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                                  title="Copiar link de convite"
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                                  Link
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => handleSendInvite(professional, index)}
                                disabled={sendingInvite || !professional.email}
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                {inviteStatus[index]?.loading ? (
                                  <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Enviando...
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Enviar convite
                                  </>
                                )}
                              </button>
                              
                              {/* Botão de copiar link */}
                              {professional.email && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); copyInviteLink(professional.tempInvitationToken, professional.email, professional, index); }}
                                  disabled={!professional.email}
                                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                  title="Copiar link de convite"
                                >
                                  <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                                  Copiar link
                                </button>
                              )}
                            </div>
                          )}
                          
                          {inviteStatus[index]?.success && (
                            <p className="text-xs text-green-600 mt-1">{inviteStatus[index].message}</p>
                          )}
                          
                          {inviteStatus[index]?.error && (
                            <p className="text-xs text-red-600 mt-1">{inviteStatus[index].message}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                <div 
                  onClick={handleAddProfessional}
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-gray-400 bg-white"
                >
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    Adicionar profissional
                  </span>
                </div>
                
                <div 
                  onClick={() => setShowBulkInviteModal(true)}
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-200 rounded-lg text-center cursor-pointer hover:border-blue-300 bg-blue-50"
                >
                  <EnvelopeIcon className="mx-auto h-12 w-12 text-blue-400" />
                  <span className="mt-2 block text-sm font-medium text-blue-600">
                    Convidar por email
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum profissional cadastrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                Comece adicionando seu primeiro profissional
              </p>
              <div className="mt-6 flex justify-center space-x-3">
                <button
                  type="button"
                  onClick={() => setShowBulkInviteModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <EnvelopeIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                  Convidar por email
                </button>
                <button
                  type="button"
                  onClick={handleAddProfessional}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Adicionar profissional
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            {currentProfessional !== null ? 'Editar Profissional' : 'Novo Profissional'}
          </h3>
          
          <form onSubmit={handleFormSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="md:col-span-2 space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={professionalForm.name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-lg`}
                    required
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={professionalForm.email}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${formErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-lg`}
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Necessário para enviar convite de acesso à plataforma
                  </p>
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={professionalForm.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(00) 00000-0000"
                    maxLength="15"
                  />
                </div>
                
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                    Biografia (opcional)
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    rows="3"
                    value={professionalForm.bio}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Uma breve descrição sobre o profissional..."
                  ></textarea>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Foto (opcional)
                </label>
                <div className="mt-1 flex flex-col items-center">
                  <div className="h-32 w-32 relative rounded-full overflow-hidden bg-gray-100 mb-4">
                    {professionalForm.photoURL ? (
                      <img 
                        src={professionalForm.photoURL} 
                        alt="Preview" 
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center bg-gray-100 text-gray-300">
                        <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <label
                    htmlFor="photo-upload"
                    className="cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                  >
                    <span>Fazer upload</span>
                    <input 
                      id="photo-upload" 
                      name="photo" 
                      type="file" 
                      className="sr-only" 
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Serviços que este profissional realiza
              </h4>
              
              <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                {Object.entries(servicesByCategory).map(([categoryName, services]) => (
                  <div key={categoryName}>
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      {categoryName}
                    </h5>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      {services.map((service) => (
                        <div 
                          key={service.id} 
                          className="flex items-start py-2 px-2 hover:bg-blue-50 rounded cursor-pointer transition-colors"
                          onClick={() => handleServiceToggle(service.id)}
                        >
                          <input
                            id={`service-${service.id}`}
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                            checked={professionalForm.serviceIds.includes(service.id)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <label 
                            htmlFor={`service-${service.id}`} 
                            className="ml-3 cursor-pointer flex-grow"
                          >
                            <span className="block text-sm font-medium text-gray-700">{service.name}</span>
                            <span className="block text-xs text-gray-500">
                              {service.duration} min • R$ {service.price}
                            </span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleFormCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Modal para convite em massa */}
      {showBulkInviteModal && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Convidar profissionais por email
                      </h3>
                      <button
                        type="button"
                        onClick={handleCloseBulkInviteModal}
                        className="bg-white rounded-md text-gray-400 hover:text-gray-500"
                      >
                        <span className="sr-only">Fechar</span>
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    
                    {bulkInviteStatus.loading ? (
                      <div className="space-y-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center">
                            <svg className="animate-spin mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>
                              Enviando convites... {bulkInviteStatus.processedCount}/{bulkInviteStatus.totalCount}
                            </span>
                          </div>
                        </div>
                        
                        <div className="max-h-60 overflow-y-auto mt-4">
                          {bulkInviteStatus.results.map((result, idx) => (
                            <div key={idx} className={`py-2 px-3 mb-1 rounded-md text-sm ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                              <div className="flex items-center">
                                {result.success ? (
                                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                                ) : (
                                  <XMarkIcon className="h-5 w-5 mr-2 text-red-500" />
                                )}
                                <span>{result.email}</span>
                              </div>
                              {!result.success && result.error && (
                                <p className="ml-7 text-xs mt-1">{result.error}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : bulkInviteStatus.results.length > 0 ? (
                      <div className="space-y-4">
                        <div className="bg-green-50 rounded-lg p-4">
                          <div className="flex items-center">
                            <CheckCircleIcon className="mr-3 h-5 w-5 text-green-500" />
                            <span>
                              Convites enviados: {bulkInviteStatus.results.filter(r => r.success).length}/{bulkInviteStatus.totalCount}
                            </span>
                          </div>
                        </div>
                        
                        <div className="max-h-60 overflow-y-auto mt-4">
                          {bulkInviteStatus.results.map((result, idx) => (
                            <div key={idx} className={`py-2 px-3 mb-1 rounded-md text-sm ${result.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                              <div className="flex items-center">
                                {result.success ? (
                                  <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                                ) : (
                                  <XMarkIcon className="h-5 w-5 mr-2 text-red-500" />
                                )}
                                <span>{result.email}</span>
                              </div>
                              {!result.success && result.error && (
                                <p className="ml-7 text-xs mt-1">{result.error}</p>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        <button
                          type="button"
                          onClick={handleCloseBulkInviteModal}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                        >
                          Fechar
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-gray-500 mb-4">
                          Digite ou cole uma lista de emails separados por vírgula, ponto e vírgula ou espaço. Um convite será enviado para cada email válido.
                        </p>
                        
                        <div>
                          <label htmlFor="bulk-emails" className="sr-only">Emails</label>
                          <div className="border border-gray-300 rounded-md px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
                            <div className="flex flex-wrap gap-1 mb-1">
                              {emailList.map((item, index) => (
                                <div 
                                  key={index} 
                                  className={`inline-flex items-center px-2 py-1 rounded-md text-sm 
                                    ${item.isValid ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'} 
                                    ${editingEmailIndex === index ? 'ring-2 ring-blue-500' : ''}`}
                                  onClick={() => editEmail(index)}
                                >
                                  <span>{item.email}</span>
                                  <button
                                    type="button"
                                    className="ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeEmail(index);
                                    }}
                                  >
                                    <span className="sr-only">Remover</span>
                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                            <input
                              id="bulk-emails"
                              name="emails"
                              type="text"
                              className="block w-full border-0 py-2 px-0 focus:ring-0 text-sm min-h-[38px]"
                              placeholder={emailList.length === 0 ? "exemplo@email.com, outro@email.com" : "Digite um email e pressione espaço"}
                              value={currentEmail}
                              onChange={handleEmailInputChange}
                              onKeyDown={handleEmailKeyDown}
                              onBlur={() => {
                                if (editingEmailIndex !== null) {
                                  saveEditingEmail();
                                } else if (currentEmail) {
                                  processCurrentEmail();
                                }
                              }}
                              ref={emailInputRef}
                            />
                          </div>
                          <p className="mt-1 text-xs text-gray-500">
                            Pressione espaço, vírgula ou Enter após cada email
                          </p>
                        </div>
                        
                        {bulkInviteStatus.error && (
                          <div className="mt-2 text-sm text-red-600">
                            {bulkInviteStatus.error}
                          </div>
                        )}
                        
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                          <button
                            type="button"
                            onClick={handleBulkInvite}
                            disabled={emailList.filter(item => item.isValid).length === 0}
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                          >
                            Enviar convites
                          </button>
                          <button
                            type="button"
                            onClick={handleCloseBulkInviteModal}
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {!showForm && (
        <div className="flex justify-between mt-8">
          <Button 
            type="button" 
            variant="outlined"
            onClick={onBack}
            disabled={isLoading}
          >
            Voltar
          </Button>
          
          <Button 
            type="button" 
            onClick={handleContinue}
            disabled={isLoading}
            isLoading={isLoading}
          >
            {professionals.length === 0 ? 'Pular esta etapa' : 'Continuar'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfessionalsStep; 